/**
 * The editorial standard, v1. This is the single source of the rubric text.
 * `docs/reference/standard-editorial.md` describes it in prose; this constant is what is
 * actually sent to the reviewers. Never fork the wording between the two.
 *
 * These strings sit in the cached prefix of every review request, so keep them
 * stable: any byte change invalidates the prompt cache for every feed at once.
 *
 * **The clauses are DATA, and the prompt is generated from them.** They were a
 * prose blob until 2026-08-29, which meant the rules a stream is judged against
 * existed only inside a template literal: unpublishable, uncountable, and
 * impossible to attribute a ruling to. `CLAUSES` below is the same text, one
 * entry per rule, and `VISUAL_RULES` and `SPEECH_RULES` are assembled from it.
 *
 * The decomposition changed no prompt. `scripts/units.ts` hashes the generated
 * strings and requires them to equal the measured ones byte for byte, so the
 * benchmark results in `docs/reference/local-filter-measurements.md` still describe the
 * text that ships. Changing a clause is therefore a deliberate act: the hash
 * fails, and it should not be updated without re-running the benchmark and
 * bumping `RUBRIC_VERSION`.
 */

export type ClauseGate = 'visual' | 'speech';

/** A rule that rejects, or a permission that exists to stop over-rejection. */
export type ClauseKind = 'reject' | 'permitted';

/**
 * Whether a LIVE RULING may name this clause in public.
 *
 * The standard itself is public in full, always: every clause below is served
 * by `GET /api/standard` and nothing here is hidden. This field is narrower and
 * is about attribution, not about the rule.
 *
 * Some clauses exist to keep a person's situation off the broadcast, and naming
 * the clause on a public slate puts it back. "Held under: an identifiable child
 * in evident distress" tells several hundred viewers that a child is in
 * distress, which is the exact disclosure the clause exists to prevent. The
 * same is true of a medical emergency, of personal information, and of an
 * accusation about a named person.
 *
 * So `operator` clauses are shown in full to the publisher, who needs to fix
 * the shot and is the only person who can, and the public slate says which GATE
 * held the feed and links to the standard. That keeps the bar checkable without
 * making the slate a second disclosure. `public` clauses are about the
 * broadcast or the operator's conduct and can be named to anyone.
 */
export type Disclosure = 'public' | 'operator';

export interface Clause {
  /** Stable and greppable. Survives rewording of the text below. */
  code: string;
  gate: ClauseGate;
  kind: ClauseKind;
  /**
   * Verbatim, exactly as sent to the reviewer, including the line wrapping.
   * The prompt is `- ${text}` per clause, so this is not free to reformat.
   */
  text: string;
  disclosure: Disclosure;
}

export const CLAUSES: readonly Clause[] = [
  // --- gate 1, picture -------------------------------------------------------
  {
    code: 'visual.not-the-event',
    gate: 'visual', kind: 'reject', disclosure: 'public',
    text: `The camera is showing nothing at all: pointed at the floor, ceiling, a wall,
  the inside of a pocket, a lens cap, or a blank frame or test pattern. This is
  about the camera doing its job, never about what the event happens to be.`,
  },
  {
    code: 'visual.undress',
    gate: 'visual', kind: 'reject', disclosure: 'operator',
    text: `A person in a state of undress, or any content of a sexual nature.`,
  },
  {
    code: 'visual.medical-or-fight',
    gate: 'visual', kind: 'reject', disclosure: 'operator',
    text: `A medical emergency, a serious injury being attended to, or a physical fight.`,
  },
  {
    code: 'visual.child-distress',
    gate: 'visual', kind: 'reject', disclosure: 'operator',
    text: `An identifiable child in evident distress.`,
  },
  {
    code: 'visual.personal-information',
    gate: 'visual', kind: 'reject', disclosure: 'operator',
    text: `A readable screen, document, badge or form showing someone's personal
  information: an address, a phone number, a card number, a medical detail.`,
  },
  {
    code: 'visual.slur-or-attack',
    gate: 'visual', kind: 'reject', disclosure: 'public',
    text: `Text or signage in frame carrying a slur or a targeted attack on a person.`,
  },
  {
    code: 'visual.surveillance',
    gate: 'visual', kind: 'reject', disclosure: 'public',
    text: `Anything that reads as surveillance of specific spectators rather than
  coverage of the event.`,
  },
  {
    code: 'visual.permitted.craft',
    gate: 'visual', kind: 'permitted', disclosure: 'public',
    text: `Poor framing, bad exposure, wobble, or the action leaving frame briefly.`,
  },
  {
    code: 'visual.permitted.ordinary-subject',
    gate: 'visual', kind: 'permitted', disclosure: 'public',
    text: `The subject being ordinary, quiet, slow, or not what you expected. A meeting,
  a rehearsal, a speech, a person talking to the camera and a half-empty room
  are all legitimate community broadcast.`,
  },
  {
    code: 'visual.permitted.ordinary-coverage',
    gate: 'visual', kind: 'permitted', disclosure: 'public',
    text: `Ordinary crowd shots, benches, scoreboards, warmups, timeouts, empty play.`,
  },
  {
    code: 'visual.permitted.physical-play',
    gate: 'visual', kind: 'permitted', disclosure: 'public',
    text: `Routine contested physical play, a hard foul, or a player going down and
  getting straight back up.`,
  },

  // --- gate 2, speech --------------------------------------------------------
  {
    code: 'speech.slur',
    gate: 'speech', kind: 'reject', disclosure: 'public',
    text: `A slur or hate speech directed at anyone, including as a quotation or a joke.`,
  },
  {
    code: 'speech.profanity',
    gate: 'speech', kind: 'reject', disclosure: 'public',
    text: `Sustained profanity, or profanity aimed at a person. Isolated mild swearing
  in the ambient crowd noise is not grounds to reject.`,
  },
  {
    code: 'speech.minor-named',
    gate: 'speech', kind: 'reject', disclosure: 'operator',
    text: `Naming a minor in a negative, accusatory, mocking or sexualised way.`,
  },
  {
    code: 'speech.accusation',
    gate: 'speech', kind: 'reject', disclosure: 'operator',
    text: `An accusation of a crime, cheating, or misconduct stated as fact about a
  named person.`,
  },
  {
    code: 'speech.medical-claim',
    gate: 'speech', kind: 'reject', disclosure: 'operator',
    text: `A medical claim about a specific person: naming an injury, a diagnosis, or a
  condition. Saying someone is hurt is fine; saying what is broken is not.`,
  },
  {
    code: 'speech.personal-information',
    gate: 'speech', kind: 'reject', disclosure: 'operator',
    text: `Someone's personal information read aloud: address, phone number, workplace.`,
  },
  {
    code: 'speech.betting',
    gate: 'speech', kind: 'reject', disclosure: 'public',
    text: `Promotion of betting, odds, or a wager on this event.`,
  },
  {
    code: 'speech.harassment',
    gate: 'speech', kind: 'reject', disclosure: 'public',
    text: `Sustained harassment of an official, a coach or a player.`,
  },
  {
    code: 'speech.permitted.partisan',
    gate: 'speech', kind: 'permitted', disclosure: 'public',
    text: `Ordinary partisan commentary, disappointment, or loud complaints about a call.`,
  },
  {
    code: 'speech.permitted.wrong',
    gate: 'speech', kind: 'permitted', disclosure: 'public',
    text: `Getting a name, a score or a rule wrong. Being uninformed is not a violation.`,
  },
  {
    code: 'speech.permitted.quiet',
    gate: 'speech', kind: 'permitted', disclosure: 'public',
    text: `Silence, crowd noise, or a transcript with almost nothing in it.`,
  },
  {
    code: 'speech.permitted.garbled',
    gate: 'speech', kind: 'permitted', disclosure: 'public',
    text: `Fragmentary or garbled transcription. Judge what is legible; if too little is
  legible to judge, that is not a violation, so accept.`,
  },
];

export function clausesFor(gate: ClauseGate, kind: ClauseKind): readonly Clause[] {
  return CLAUSES.filter((c) => c.gate === gate && c.kind === kind);
}

/** One clause by code. Null rather than throwing: an unknown code is data, not a crash. */
export function clauseByCode(code: string): Clause | null {
  return CLAUSES.find((c) => c.code === code) ?? null;
}

const bullets = (cs: readonly Clause[]): string => cs.map((c) => `- ${c.text}`).join('\n');

/**
 * The two commitments, published on the public site and interpolated into the
 * reviewer's prompt below, so there is exactly ONE copy of them.
 *
 * **Extracted byte-identically on 2026-08-30, and that was a deliberate
 * departure from the plan.** The public-site plan's 4.2 asked for these to be
 * rephrased into the third person, accepted that this moves
 * `MEASURED_PROMPT_SHA.house`, and prescribed the re-measure:
 * `tsx scripts/probe-filter.ts` plus `npm run corpus && npm run bench:real`.
 *
 * **Neither of those instruments touches `HOUSE_RULES`.** It is consumed by
 * `review/visual.ts` and `review/speech.ts`, which are the REMOTE JUDGE paths;
 * `probe-filter.ts` and `bench-real.ts` both drive `TriageFilter`, whose prompt
 * this constant never reaches. Following the plan literally would have shipped a
 * moved judge prompt certified by two benchmarks that cannot see it, which is
 * the same shape as a guard asserting the inverse of its invariant. The
 * instrument that WOULD see it is `probe-judge.ts`, and four frames is not a
 * corpus.
 *
 * So the split reassembles the identical string and the hash below stays green,
 * which is a stronger guarantee than any re-measure: the prompt did not change,
 * and `sha(HOUSE_RULES) === MEASURED_PROMPT_SHA.house` proves it on every run.
 *
 * The second person survives, and the page frames it rather than rewriting it:
 * these are published as the instruction the reviewer is literally given. A
 * third-person paraphrase would have been a second rendering of one commitment,
 * which is the drift this whole file is arranged against, and it would have been
 * strictly less informative than the text itself.
 */
export const COMMITMENTS = `1. The default is NOT to broadcast. If you are unsure, reject. Going dark is a
   normal, acceptable outcome here and carries no penalty. Putting something
   unacceptable to air is the only real failure.
2. You are judging THIS material, not the operator. Do not speculate about
   intent, and do not reject for being amateurish. Shaky, plain and dull is
   fine. Community broadcast is supposed to look like the community.`;

export const HOUSE_RULES = `You are a broadcast standards reviewer for a community news organization.
You are reviewing a live spectator feed of a community event to decide whether
it may go to air right now. It could be anything the community is doing: a
game, a performance, a meeting, a march, a celebration, or a person talking to
the camera. What the event IS is never a reason to reject it.

Two principles govern every call you make:

${COMMITMENTS}`;

export const VISUAL_RULES = `Judge only what you can see in these frames.

REJECT if any of the following is visible:
${bullets(clausesFor('visual', 'reject'))}

ACCEPT otherwise. Specifically, these are NOT grounds to reject:
${bullets(clausesFor('visual', 'permitted'))}`;

export const SPEECH_RULES = `Judge only the words in this transcript.

REJECT if the transcript contains any of the following:
${bullets(clausesFor('speech', 'reject'))}

ACCEPT otherwise. Specifically, these are NOT grounds to reject:
${bullets(clausesFor('speech', 'permitted'))}`;

/**
 * The measured prompts, by hash.
 *
 * These are the sha256 prefixes of the strings the benchmarks in
 * `docs/reference/local-filter-measurements.md` and `docs/reference/two-stage-filter.md` were run
 * against. `scripts/units.ts` requires the generated text above to match, which
 * is what makes the clause table a faithful decomposition rather than a rewrite
 * that happens to look similar.
 *
 * A failing hash is not a bug to be silenced by updating the constant. It means
 * the rules changed, so the benchmark has to be re-run and `RUBRIC_VERSION`
 * bumped, and every cached verdict is about a standard that no longer exists.
 */
export const MEASURED_PROMPT_SHA = {
  // `REMEASURED` 2026-08-29, and this is the first time this gate has fired for
  // real. Two changes broadened the standard from a SPORTING event to a
  // COMMUNITY event: the house rules now say the subject can be a game, a
  // performance, a meeting, a march, a celebration or a person talking to the
  // camera, and "what the event IS is never a reason to reject it"; and the
  // picture rules gained a permitted clause saying an ordinary, quiet or slow
  // subject is legitimate broadcast. The reject clause about not showing the
  // event was reworded to be about the camera doing its job rather than about
  // what is in front of it.
  //
  // The gate did exactly what it exists for: the change arrived on one branch
  // and the hash on another, and merging them made the difference impossible to
  // miss. Per the note above, the constants were NOT updated to silence it. The
  // filter benchmark was re-run first through the shipped code path
  // (`scripts/probe-filter.ts`, both gates, the real models): 9 of 10 correct,
  // and the tenth was the `court.jpg` miss that issue 011 tracked.
  //
  // **Both halves are now done, 2026-08-29, and the tenth turned out not to be
  // a miss at all.** `court.jpg` was a flat drawing that its own generator
  // called "a plausible gym" while the probe expected it REJECTED as
  // not-the-event; it was neither, and the disagreement between the tiers was
  // an artefact of an out-of-distribution frame rather than a finding. Rebased
  // onto real footage, `probe-filter` scores **10 of 10** and `probe-judge`
  // **6 of 6**, with both tiers agreeing on every fixture.
  //
  // `bench:real` HAS now been re-run against this text on real footage:
  // **14 of 15 approved with the 15th correctly rejected**, unchanged, so the
  // broadening rejected nothing it should have accepted and accepted nothing it
  // should have rejected. `RUBRIC_VERSION` is bumped below.
  house: 'd3f4239e8a9b833b',
  visual: '7a5abe0a7617408c',
  // Unchanged, and worth noticing: the speech rules were not touched by the
  // broadening, so its hash still matches the text every speech measurement was
  // taken against.
  speech: 'd04e1f816cc569a1',
} as const;

/**
 * Bumped whenever the rules above change, so cached verdicts can be dropped.
 *
 * `v1.1.0` on 2026-08-29: the standard broadened from a sporting event to any
 * community event. A minor bump rather than a major one because nothing that was
 * acceptable became unacceptable: the reject clauses are unchanged in substance
 * and one permission was added. A verdict cached under v1.0.0 was taken against
 * a NARROWER standard, so it may have rejected something this one permits, which
 * is why it still has to be dropped rather than carried forward.
 */
/**
 * A CLAUSE GOVERNS ITS OWN MODALITY AND NOTHING ELSE.
 *
 * Decided 2026-08-31 by the operator, answering §6 of
 * `docs/plans/2026-08-30-picture-and-sound-as-two-decisions.md`, which laid out
 * three readings and deliberately picked none.
 *
 * **The answer is full independence, and the reason is symmetry.** A feed whose
 * SOUND the standard refused still supplies its picture, which is stage 2 and
 * the whole point of the plan. So a feed whose PICTURE it refused still supplies
 * its sound. The opposite of one is the other.
 *
 * **This document's own recommendation was the other way and was wrong.** It
 * argued that four of the five operator-only clauses are on the picture side and
 * a child in evident distress is usually audible, so an operator-only picture
 * rejection should remove the feed from both decisions. That makes the picture
 * gate's ruling govern the sound, which is gate 1 deciding gate 2's business,
 * and this repository forbids exactly that: *"Gates 1 and 2 must never see each
 * other's material. Two gates are only worth having if they fail
 * independently."* The sharper version is that the audio in question **has
 * already passed its own gate**: the speech reviewer read that transcript and
 * approved it. Refusing it for what the picture showed overrides one gate's
 * ruling with another's.
 *
 * **The residual, recorded because it was decided with rather than against.**
 * `SPEECH_RULES` says "Judge only the words in this transcript", so a wordless
 * sound is reviewed by nothing. That is true of an ordinary broadcast too, where
 * a feed on air carries its own unreviewed non-verbal audio. What independence
 * ADDS is a path: sound from a feed whose picture was refused can now accompany
 * a different picture. If that ever needs closing it is a change to what gate 2
 * JUDGES, not a coupling between the gates.
 *
 * Nothing in `Clause` encodes this, deliberately. The answer is structural
 * rather than per-clause, so a field would be a second copy of a rule the two
 * predicates already express by what they read. Guard `SND12`.
 */
export const MODALITY_INDEPENDENCE = 'a clause governs its own modality and nothing else';

export const RUBRIC_VERSION = 'v1.1.0';

/** One published version of the rules, and what changed when it took effect. */
export interface RubricRelease {
  version: string;
  /** ISO date the version took effect. */
  effective: string;
  /** What changed, in words a publisher can act on. */
  changed: string;
}

/**
 * The published version history, newest first.
 *
 * **A standard that can be edited silently is a standard the reader has to take
 * on trust again**, which is the thing publishing it removes. Rulings carry
 * `rubricVersion`, so the moment one is appealed the reader needs the version it
 * was made under, and adding this after versions are already cited means
 * reconstructing it from git.
 *
 * It lives beside `RUBRIC_VERSION` rather than in the site, so bumping the
 * version without recording the change fails a gate rather than passing as an
 * omission. `SITE10` requires the head of this list to BE `RUBRIC_VERSION`.
 *
 * v1.0.0's clause text is deliberately not reconstructed: the history says what
 * changed and does not pretend to serve text it no longer has. Frozen pages ship
 * forward-only from v1.1.0.
 */
export const VERSION_HISTORY: readonly RubricRelease[] = [
  {
    version: 'v1.1.0',
    effective: '2026-08-29',
    changed: `The standard broadened from a SPORTING event to any COMMUNITY event. The `
      + `reviewer is now told the subject may be a game, a performance, a meeting, a march, a `
      + `celebration or a person talking to the camera, and that what the event IS is never a `
      + `reason to reject it. One permission was added, that an ordinary, quiet or slow subject `
      + `is legitimate community broadcast, and the clause about the camera not showing the `
      + `event was reworded to be about the camera doing its job rather than about what is in `
      + `front of it. A minor bump because nothing that was acceptable became unacceptable. `
      + `Verdicts cached under v1.0.0 were still dropped, because they were taken against a `
      + `NARROWER standard and may have rejected something this one permits.`,
  },
  {
    version: 'v1.0.0',
    effective: '2026-08-28',
    changed: `The first published rubric: the reject clauses and permissions for the picture `
      + `and speech gates, written for a sporting event.`,
  },
];

/** A version string a ruling may carry, or null if it names no published version. */
export function releaseFor(version: string): RubricRelease | null {
  return VERSION_HISTORY.find((r) => r.version === version) ?? null;
}
